import React, { FC } from 'react'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { ReactComponent as CookiesImg } from '../../images/privacy/cookies.svg'
import { PurpleButton } from '../../components/Buttons/PurpleButton'

const useStyles = makeStyles((theme: Theme) => ({
  main: {
    width: '100%',
    minHeight: 'calc(100vh - 248px)',
    paddingTop: 176,
  },
  container: {
    maxWidth: 1184,
    margin: '0 auto',
    paddingTop: 64,
    borderTop: '1px solid rgba(0,0,0,0.24)',
  },
  cookiesImg: {
    boxSizing: 'content-box',
    float: 'right',
    paddingLeft: 130,
  },
  title: {
    marginTop: 0,
    paddingTop: 5,
    paddingBottom: 30,
    fontSize: 40,
    fontWeight: 'bold',
    letterSpacing: 0,
    color: theme.palette.primary.dark,
  },
  subtitle: {
    padding: '45px 0 20px',
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.primary.dark,
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
  },
  list: {
    marginBottom: 0,
    paddingLeft: 20,
    paddingTop: 5,
    listStyle: 'none',
    '& li': {
      paddingBottom: 8,
      fontFamily: 'Montserrat, sans-serif',
      fontSize: 16,
      fontWeight: 500,
      '&::before': {
        display: 'inline-block',
        content: '""',
        width: 8,
        height: 8,
        margin: '0 12px 0 -20px',
        borderRadius: '50%',
        background: theme.palette.secondary.light,
      },
    },
  },
  button: {
    display: 'block',
    width: 'max-content',
    margin: '54px auto',
    padding: '14px 45px',
    fontFamily: 'Montserrat, san-serif',
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: theme.palette.info.light,
    background: theme.palette.primary.main,
    border: `1px solid ${theme.palette.info.light}`,
    borderRadius: 28,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}))

export const PrivacyPolicy: FC = () => {
  const classes = useStyles()
  return (
    <main className={classes.main}>
      <Grid item lg className={classes.container}>
        <CookiesImg className={classes.cookiesImg} />

        <Typography variant="h1" gutterBottom className={classes.title}>
          Privacy Policy & Cookie Policy
        </Typography>
        <Typography className={classes.text}>
          The privacy notice explains how Sparling Financial (hereafter referred to as “SPARLING”) collects, uses,
          discloses, retains and secures your personal data as part of its business practices. The policy clearly
          articulates the legal justifications for the processing of your personal data and also lists your data subject
          rights under the Cayman Islands’ Data Protection Law, 2017 (“DPL”) and the General Data Protection Regulation
          (“GDPR”).
        </Typography>

        <Typography variant="h2" gutterBottom className={classes.subtitle}>
          Overview
        </Typography>
        <Typography className={classes.text}>
          SPARLING respects your privacy, and you are entitled to have your personal data processed in accordance with
          the DPL. The key principles SPARLING applies when processing your personal data are as follows:
        </Typography>

        <ul className={classes.list}>
          <li>Lawfulness: SPARLING will only collect personal data in a fair, lawful and transparent manner.</li>
          <li>
            Data minimisation: SPARLING will limit the collection of personal data to what is directly relevant and
            necessary for the services provided.
          </li>
          <li>
            Purpose limitation: SPARLING will only collect personal data for specified, explicit and legitimate
            purposes.
          </li>
          <li>
            Accuracy: SPARLING will keep personal data accurate and up to date while there continues to be a client
            relationship, and in certain circumstances, after that relationship has ended.
          </li>
          <li>
            Data security and protection: SPARLING will implement technical and organisational measures to ensure an
            appropriate level of data security and protection considering the sensitivity of the personal data. Such
            measures provide for the prevention of any unauthorized or unlawful processing of personal data and against
            accidental loss or destruction of, or damage to that data.
          </li>
          <li>Access and rectification: SPARLING will process personal data in line with clients’ legal rights.</li>
          <li>
            Retention limitation: SPARLING will retain personal data in a manner consistent with the applicable DPL and
            DPL Regulations and no longer than is necessary for the purposes for which it has been collected in
            accordance withits retention policy.
          </li>
          <li>
            Protection for international transfers: SPARLING will ensure that if personal data is transferred, it is
            adequately protected.
          </li>
        </ul>

        <Typography variant="h2" gutterBottom className={classes.subtitle}>
          What personal data does SPARLING collect?
        </Typography>
        <Typography className={classes.text}>
          SPARLING collects various personal data which may include the following (this list is not exhaustive):
        </Typography>

        <ul className={classes.list}>
          <li>Name and Address</li>
          <li>Date of birth</li>
          <li>Telephone number</li>
          <li>Email address</li>
          <li>Copy of passport photo / Biographical data page</li>
          <li>Financial information included your method of payment such as check or wire transfer to SPARLING</li>
        </ul>

        <Typography variant="h2" gutterBottom className={classes.subtitle}>
          How does SPARLING use the personal data it collects?
        </Typography>
        <Typography className={classes.text}>
          SPARLING may use your personal data to (this list is not exhaustive):
        </Typography>

        <ul className={classes.list}>
          <li>Respond to client inquiries</li>
          <li>Manage the client relationship</li>
          <li>Send invoices and collect payment for services rendered</li>
          <li>Conduct promotional activities</li>
          <li>Market services</li>
          <li>Handle complaints</li>
          <li>Manage client’s wallets</li>
          <li>Prevent fraud or other criminal activity</li>
          <li>Record health and safety details if there is an incident at the SPARLING office</li>
        </ul>

        <PurpleButton width="max-content" mt={54} mb={54} href={'/files/Privacy_Policy.pdf'}>
          Load more
        </PurpleButton>
      </Grid>
    </main>
  )
}
